import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import MegaMenu from './MegaMenu';
import { featuresMenu } from './menuConfig';

const logo = '/images/logos/Hairchitech_Logo1.png';

const PublicNavbar = ({ onSignInClick }) => {
  const { t } = useTranslation('marketing');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMegaOpen, setIsMegaOpen] = useState(false);
  const [isMobileFeaturesOpen, setIsMobileFeaturesOpen] = useState(false);
  const megaCloseTimeoutRef = useRef(null);

  const handleMegaEnter = () => {
    if (megaCloseTimeoutRef.current) {
      clearTimeout(megaCloseTimeoutRef.current);
      megaCloseTimeoutRef.current = null;
    }

    setIsMegaOpen(true);
  };

  const handleMegaLeave = () => {
    megaCloseTimeoutRef.current = setTimeout(() => {
      setIsMegaOpen(false);
      megaCloseTimeoutRef.current = null;
    }, 120);
  };

  useEffect(() => {
    return () => {
      if (megaCloseTimeoutRef.current) {
        clearTimeout(megaCloseTimeoutRef.current);
      }
    };
  }, []);

  const SignInControl = ({ mobile = false }) => {
    const className = mobile
      ? 'px-4 py-2 text-sm font-medium text-white text-center transition-all duration-200 hover:opacity-80'
      : 'hidden sm:inline-flex px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:opacity-80';

    if (typeof onSignInClick === 'function') {
      return (
        <button onClick={onSignInClick} className={className}>
          {t('marketing.nav_sign_in')}
        </button>
      );
    }

    return (
      <Link to="/?auth=1" className={className}>
        {t('marketing.nav_sign_in')}
      </Link>
    );
  };

  return (
    <nav className="bg-transparent backdrop-blur-md border-b border-white/10 absolute top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center space-x-8">
            <div className="flex items-center">
              <Link to="/">
                <img src={logo} alt="Hairchitech" className="h-16 w-16 sm:h-20 sm:w-20 lg:h-24 lg:w-24 cursor-pointer" />
              </Link>
            </div>

            <div className="hidden lg:flex items-center space-x-8">
              <div
                className="relative"
                onMouseEnter={handleMegaEnter}
                onMouseLeave={handleMegaLeave}
              >
                <button
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-[15px] font-medium tracking-[0.01em] transition-colors ${
                    isMegaOpen ? 'text-white' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {t('marketing.nav_features')}
                  <svg
                    className={`h-4 w-4 transition-transform duration-200 ${isMegaOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <MegaMenu isOpen={isMegaOpen} />
              </div>
              <button className="text-white/80 hover:text-white px-3 py-2 text-sm font-medium transition-colors">{t('marketing.nav_solutions')}</button>
              <button className="text-white/80 hover:text-white px-3 py-2 text-sm font-medium transition-colors">{t('marketing.nav_developers')}</button>
              <Link to="/pricing" className="text-white/80 hover:text-white px-3 py-2 text-sm font-medium transition-colors">{t('marketing.nav_pricing')}</Link>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <LanguageSwitcher theme="dark" className="hidden lg:flex" />
            <SignInControl />
            <Link
              to="/pricing"
              className="hidden sm:inline-flex px-4 py-2 rounded-full text-sm font-medium text-gray-800 bg-white transition-all duration-200 hover:opacity-90"
            >
              {t('marketing.nav_view_pricing')}
            </Link>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white hover:text-white p-2 rounded-md bg-black/20 hover:bg-black/40 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-white/10 bg-black/80 backdrop-blur-sm">
            <div className="flex flex-col space-y-2 px-4 py-4">
              {/* Mobile Features accordion */}
              <div>
                <button
                  onClick={() => setIsMobileFeaturesOpen(!isMobileFeaturesOpen)}
                  className={`flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-[15px] font-medium transition-colors ${
                    isMobileFeaturesOpen
                      ? 'bg-white/[0.05] text-white'
                      : 'text-white/90 hover:bg-white/[0.04] hover:text-white'
                  }`}
                >
                  <span>{t('marketing.nav_features')}</span>
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${isMobileFeaturesOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isMobileFeaturesOpen && (
                  <div className="mt-2 px-4 pb-2">
                    {featuresMenu.map((section, si) => (
                      <div
                        key={si}
                        className={`${si > 0 ? 'mt-4 border-t border-white/10 pt-4' : ''}`}
                      >
                        <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-white/45">
                          {t(section.titleKey)}
                        </p>
                        <ul className="space-y-0">
                          {section.items.map((item, ii) => (
                            <li
                              key={ii}
                              className={`cursor-pointer py-2.5 ${
                                ii > 0 ? 'border-t border-white/10' : ''
                              }`}
                            >
                              <p className="text-[15px] font-medium leading-5 text-white transition-colors hover:text-[#c9a038]">
                                {t(item.labelKey)}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button className="rounded-2xl px-4 py-3 text-left text-[15px] font-medium text-white/90 transition-colors hover:bg-white/[0.04] hover:text-white">
                {t('marketing.nav_solutions')}
              </button>
              <button className="rounded-2xl px-4 py-3 text-left text-[15px] font-medium text-white/90 transition-colors hover:bg-white/[0.04] hover:text-white">
                {t('marketing.nav_developers')}
              </button>
              <Link
                to="/pricing"
                className="rounded-2xl px-4 py-3 text-left text-[15px] font-medium text-white/90 transition-colors hover:bg-white/[0.04] hover:text-white"
              >
                {t('marketing.nav_pricing')}
              </Link>
              <div className="flex flex-col space-y-3 px-4">
                <SignInControl mobile />
                <Link to="/pricing" className="px-4 py-2 rounded-full text-sm font-medium text-gray-800 bg-white text-center transition-all duration-200 hover:opacity-90">
                  {t('marketing.nav_view_pricing')}
                </Link>
                <div className="pt-1">
                  <LanguageSwitcher theme="dark" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default PublicNavbar;
